import { supabase } from '@/lib/supabase';

export interface UnassignedUser {
  id: string;
  name: string;
  email: string;
  masked_name: string;
  masked_email: string;
  created_at: string;
  referred_by_affiliate?: string;
}

export interface ActiveAffiliate {
  id: string;
  name: string;
  email: string;
  referral_code: string;
  activeReferrals: number;
}

export interface DataConsistencyReport {
  isConsistent: boolean;
  issues: DataConsistencyIssue[];
  totalUsers: number;
  inconsistentUsers: number;
  assignedUsers: number;
  unassignedUsers: number;
  summary: {
    totalUsers: number;
    usersWithInconsistencies: number;
    orphanedReferrals: number;
    missingProfiles: number;
  };
}

export interface DataConsistencyIssue {
  type: 'missing_profile' | 'orphaned_referral' | 'mismatched_affiliate';
  userId: string;
  userName?: string;
  userEmail?: string;
  issue: string;
  description: string;
  recommendedAction: string;
  data: any;
}

class UserAssignmentService {
  // Get users who haven't been assigned to an affiliate yet
  async getUnassignedUsers(): Promise<UnassignedUser[]> {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select(`
          user_id,
          display_name,
          display_email,
          created_at,
          referred_by_affiliate
        `)
        .is('referred_by_affiliate', null)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching unassigned users:', error);
        throw error;
      }

      return (data || []).map(profile => ({
        id: profile.user_id,
        name: profile.display_name || profile.display_email || 'Unknown User',
        email: profile.display_email || 'No email',
        masked_name: this.maskName(profile.display_name || profile.display_email || 'Unknown User'),
        masked_email: this.maskEmail(profile.display_email || 'No email'),
        created_at: profile.created_at,
        referred_by_affiliate: profile.referred_by_affiliate
      }));
    } catch (error) {
      console.error('Error in getUnassignedUsers:', error);
      throw error;
    }
  }

  // Get all active affiliates
  async getActiveAffiliates(): Promise<ActiveAffiliate[]> {
    try {
      const { data, error } = await supabase
        .from('affiliates')
        .select(`
          id,
          name,
          email,
          referral_code,
          status
        `)
        .eq('status', 'active');

      if (error) {
        console.error('Error fetching active affiliates:', error);
        throw error;
      }

      // For now, set activeReferrals to 0 to avoid complex queries
      // Can be enhanced later with a separate count query if needed
      return (data || []).map(affiliate => ({
        id: affiliate.id,
        name: affiliate.name || affiliate.email,
        email: affiliate.email,
        referral_code: affiliate.referral_code || 'N/A',
        activeReferrals: 0 // Simplified for now
      }));
    } catch (error) {
      console.error('Error in getActiveAffiliates:', error);
      throw error;
    }
  }

  // Assign user to affiliate (admin function)
  async assignUserToAffiliate(userId: string, affiliateId: string, referralCode: string): Promise<boolean> {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        console.error('❌ User must be authenticated to assign affiliates');
        return false;
      }

      // Check if current user is admin using RPC
      const { data: isAdmin, error: adminError } = await supabase
        .rpc('is_admin');

      if (adminError || !isAdmin) {
        console.error('❌ Only admins can assign users to affiliates');
        return false;
      }

      // Update user profile with affiliate reference
      const { error: profileError } = await supabase
        .from('user_profiles')
        .update({ referred_by_affiliate: affiliateId })
        .eq('user_id', userId);

      if (profileError) {
        console.error('❌ Error updating user profile:', profileError);
        return false;
      }

      // Create referral record
      const { error: referralError } = await supabase
        .from('referrals')
        .insert({
          referral_code: referralCode,
          referrer_id: affiliateId,
          referred_user_id: userId,
          status: 'registered', // Fixed: Use 'registered' for admin assignment
          created_at: new Date().toISOString()
        });

      if (referralError) {
        console.error('❌ Error creating referral record:', referralError);
        return false;
      }

      console.log('✅ User successfully assigned to affiliate');
      return true;
    } catch (error) {
      console.error('❌ Error in assignUserToAffiliate:', error);
      return false;
    }
  }

  // Simplified registration assignment - no authentication check needed
  async assignUserToAffiliateOnRegistration(userId: string, affiliateId: string, referralCode: string): Promise<boolean> {
    try {
      console.log('🎯 [ASSIGNMENT] Starting registration affiliate assignment for user:', userId);
      
      // STEP 1: Update user profile with affiliate reference
      console.log('📝 [ASSIGNMENT] Updating user profile...');
      const { error: profileError } = await supabase
        .from('user_profiles')
        .update({ 
          referred_by_affiliate: affiliateId,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId);

      if (profileError) {
        console.error('❌ [ASSIGNMENT] Error updating user profile:', profileError);
        return false;
      }

      // STEP 2: Create referral record
      console.log('📋 [ASSIGNMENT] Creating referral record...');
      const { error: referralError } = await supabase
        .from('referrals')
        .insert({
          referral_code: referralCode,
          referrer_id: affiliateId,
          referred_user_id: userId,
          status: 'registered', // Fixed: Use 'registered' for new user registration
          created_at: new Date().toISOString()
        });

      if (referralError) {
        console.error('❌ [ASSIGNMENT] Error creating referral record:', referralError);
        return false;
      }

      console.log('✅ [ASSIGNMENT] User successfully assigned to affiliate during registration');
      return true;
    } catch (error) {
      console.error('❌ [ASSIGNMENT] Exception in assignUserToAffiliateOnRegistration:', error);
      return false;
    }
  }

  // Transfer user from one affiliate to another (admin function)
  async transferUserToAffiliate(userId: string, newAffiliateId: string, newReferralCode: string): Promise<boolean> {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        console.error('❌ User must be authenticated to transfer affiliates');
        return false;
      }

      // Check if current user is admin using RPC
      const { data: isAdmin, error: adminError } = await supabase
        .rpc('is_admin');

      if (adminError || !isAdmin) {
        console.error('❌ Only admins can transfer users between affiliates');
        return false;
      }

      // Update user profile
      const { error: profileError } = await supabase
        .from('user_profiles')
        .update({ referred_by_affiliate: newAffiliateId })
        .eq('user_id', userId);

      if (profileError) {
        console.error('❌ Error updating user profile:', profileError);
        return false;
      }

      // Update existing referral record
      const { error: referralError } = await supabase
        .from('referrals')
        .update({
          referrer_id: newAffiliateId,
          referral_code: newReferralCode,
          updated_at: new Date().toISOString()
        })
        .eq('referred_user_id', userId);

      if (referralError) {
        console.error('❌ Error updating referral record:', referralError);
        return false;
      }

      console.log('✅ User successfully transferred to new affiliate');
      return true;
    } catch (error) {
      console.error('❌ Error in transferUserToAffiliate:', error);
      return false;
    }
  }

  // Data consistency validation
  async validateDataConsistency(): Promise<DataConsistencyReport> {
    try {
      console.log('🔍 Starting data consistency validation...');
      
      const issues: DataConsistencyIssue[] = [];
      
      // Get all users
      const { data: allUsers, error: usersError } = await supabase
        .from('user_profiles')
        .select(`
          user_id,
          display_name,
          display_email,
          referred_by_affiliate
        `);

      if (usersError) {
        console.error('Error fetching users:', usersError);
        throw usersError;
      }

      // Get all referrals
      const { data: referrals, error: referralsError } = await supabase
        .from('referrals')
        .select('referred_user_id, referrer_id, referral_code');

      if (referralsError) {
        console.error('Error fetching referrals:', referralsError);
        throw referralsError;
      }

      const referralMap = new Map(referrals?.map(r => [r.referred_user_id, r]) || []);
      let assignedUsers = 0;
      let inconsistentUsers = 0;

      // Check for users with referrals but no user_profiles.referred_by_affiliate
      for (const user of allUsers || []) {
        const hasProfileAssignment = !!user.referred_by_affiliate;
        const hasReferralRecord = referralMap.has(user.user_id);
        const referralRecord = referralMap.get(user.user_id);

        if (hasProfileAssignment) {
          assignedUsers++;
        }

        let userHasIssues = false;

        // Issue 1: Profile has assignment but no referral record
        if (hasProfileAssignment && !hasReferralRecord) {
          issues.push({
            type: 'missing_profile',
            userId: user.user_id,
            userName: user.display_name || 'Unknown',
            userEmail: user.display_email || 'Unknown',
            issue: 'Missing referral record',
            description: 'User profile shows affiliate assignment but no referral record exists',
            recommendedAction: 'Create referral record or clear profile assignment',
            data: { affiliateId: user.referred_by_affiliate }
          });
          userHasIssues = true;
        }

        // Issue 2: Referral record exists but profile not assigned
        if (!hasProfileAssignment && hasReferralRecord) {
          issues.push({
            type: 'orphaned_referral',
            userId: user.user_id,
            userName: user.display_name || 'Unknown',
            userEmail: user.display_email || 'Unknown',
            issue: 'Orphaned referral',
            description: 'Referral record exists but user profile not assigned to affiliate',
            recommendedAction: `Update profile with affiliate: ${referralRecord?.referrer_id}`,
            data: { affiliateId: referralRecord?.referrer_id }
          });
          userHasIssues = true;
        }

        // Issue 3: Mismatched assignments
        if (hasProfileAssignment && hasReferralRecord && user.referred_by_affiliate !== referralRecord?.referrer_id) {
          issues.push({
            type: 'mismatched_affiliate',
            userId: user.user_id,
            userName: user.display_name || 'Unknown',
            userEmail: user.display_email || 'Unknown',
            issue: 'Mismatched assignment',
            description: 'Profile affiliate assignment does not match referral record',
            recommendedAction: `Sync assignments - Profile: ${user.referred_by_affiliate}, Referral: ${referralRecord?.referrer_id}`,
            data: { 
              profileAffiliate: user.referred_by_affiliate,
              referralAffiliate: referralRecord?.referrer_id
            }
          });
          userHasIssues = true;
        }

        if (userHasIssues) {
          inconsistentUsers++;
        }
      }

      const totalUsers = allUsers?.length || 0;
      const unassignedUsers = totalUsers - assignedUsers;

      return {
        isConsistent: issues.length === 0,
        issues,
        totalUsers,
        inconsistentUsers,
        assignedUsers,
        unassignedUsers,
        summary: {
          totalUsers,
          usersWithInconsistencies: inconsistentUsers,
          orphanedReferrals: issues.filter(i => i.type === 'orphaned_referral').length,
          missingProfiles: issues.filter(i => i.type === 'missing_profile').length
        }
      };
    } catch (error) {
      console.error('❌ Error validating data consistency:', error);
      throw error;
    }
  }

  // Get data consistency report
  async getDataConsistencyReport(): Promise<DataConsistencyReport> {
    return this.validateDataConsistency();
  }

  // Fix data inconsistencies
  async fixDataInconsistencies(issues: DataConsistencyIssue[]): Promise<{ fixed: number; failed: number }> {
    let fixed = 0;
    let failed = 0;

    for (const issue of issues) {
      try {
        if (issue.type === 'orphaned_referral') {
          // Update user_profiles.referred_by_affiliate to match referral record
          const { error } = await supabase
            .from('user_profiles')
            .update({ referred_by_affiliate: issue.data.affiliateId })
            .eq('user_id', issue.userId);

          if (error) {
            console.error('Failed to fix orphaned referral:', error);
            failed++;
          } else {
            fixed++;
          }
        } else if (issue.type === 'missing_profile') {
          // Create referral record to match user_profiles.referred_by_affiliate
          const { error } = await supabase
            .from('referrals')
            .insert({
              referred_user_id: issue.userId,
              referrer_id: issue.data.affiliateId,
              referral_code: 'SYSTEM_GENERATED',
              status: 'registered', // Fixed: Use 'registered' for system-generated records
              created_at: new Date().toISOString()
            });

          if (error) {
            console.error('Failed to create missing referral:', error);
            failed++;
          } else {
            fixed++;
          }
        }
      } catch (error) {
        console.error('Error fixing issue:', error);
        failed++;
      }
    }

    return { fixed, failed };
  }

  // Helper methods for masking sensitive data
  private maskEmail(email: string): string {
    const [localPart, domain] = email.split('@');
    const maskedLocal = localPart.length > 2 
      ? localPart.substring(0, 2) + '*'.repeat(localPart.length - 2)
      : localPart;
    return `${maskedLocal}@${domain}`;
  }

  private maskName(name: string): string {
    if (name.length <= 2) return name;
    return name.substring(0, 2) + '*'.repeat(name.length - 2);
  }
}

export const userAssignmentService = new UserAssignmentService();
